import React, { useState, useEffect } from 'react';
import { Container, Col, Row, Button, Spinner } from "reactstrap"
import { addBodyClasses } from "../utils";
import { JobContext } from "../../contexts/job";
import ScreenCaptureBlock from "./ScreenCaptureBlock";
import { WORKSHOP_BASE_URL, TAXONOMY_BASE_URL } from "../../utils";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import axios from "axios";
import SumSubInstructions from '../Retro/SumSub';

const queryString = require('query-string');
const jsonLogic = require('json-logic-js');
const platformjs = require('platform');

function JobClient({embedded}) {
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [hasProcessedFinish, setHasProcessedFinish] = useState(false);
  const [job, setJob] = useState(null);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [responseUUID, setResponseUUID] = useState(null);
  const [terminated, setTerminated] = useState(false);
  const [hasInitializedEmbed, setHasInitializedEmbed] = useState(!embedded);
  const [fallbackLinkCompletionStatus, setFallbackLinkCompletionStatus] = useState(null);
  const [fallbackLinkClicks, setFallbackLinkClicks] = useState(0);
  const [needsValidation, setNeedsValidation] = useState(true);
  const [validatedQuestionList, setValidatedQuestionList] = useState({});
  const [completedRequiredBlocks, setCompletedRequiredBlocks] = useState(false);

  // temporary validation bits ... needs to be generalized ... only works for a single question
  const [questionValidationStatus, setQuestionValidationStatus] = useState('invalid');

  const parsed = queryString.parse(window.location.search);
  const debug = parsed.debug === '1';
  const uuid = parsed.uuid;
  const cid = parsed.cid || '';
  const srid = parsed.srid || '';

  const partial_payment = parsed.partial_payment || 'false';
  const redirect = parsed.redirect || 'false';

  const lang = (parsed.lang ? parsed.lang.toLowerCase() : null) || 'en';
  const baseJobURL = `${WORKSHOP_BASE_URL}/j/${uuid}`;

  // We have some (optional) custom code for each integration, so we track host
  const embeddingHost = parsed.host || '';

  // When running "direct" we need to track and pay users, when indirect (default) that is happening elsewhere
  const runType = parsed.type || 'indirect';

  // Prepopulate the survey with data - this is mostly for
  const startingData = parsed.data ? JSON.parse(parsed.data) : {};
  const [values, setValues] = useState(startingData);

  // For profile surveys
  const profileJobName = parsed.profile || '';
  const isProfileSurvey = profileJobName !== '';

  // For testing...
  const test = parsed.test === '1';
  const exampleJobName = parsed.example || '';

  const postTrackingToWorkshop = !test && !isProfileSurvey;

  const getString = (name, lang) => {
    return {
      finished_the_job: {
        en: 'Finished the job, thanks!',
        pt: 'Terminei o trabalho, obrigado!',
        es: 'Terminado el trabajo, gracias!',
        id: 'Menyelesaikan pekerjaan, terima kasih!',
        fr: "Travail terminé, merci !",
        de: "Die Arbeit ist erledigt, danke!",
        it: "Lavoro finito, grazie!",
      },
      project_not_running: {
        en: 'This project is not running',
        pt: 'Este projeto não está em execução',
        es: 'Este proyecto no se está ejecutando',
        id: 'Proyek ini tidak berjalan',
        fr: "Ce projet n'est pas en cours d'exécution",
        de: "Dieses Projekt läuft nicht",
        it: "Questo progetto non è in esecuzione",
      },
      project_closed: {
        en: 'This project is closed',
        pt: 'Este projeto está fechado',
        es: 'Este proyecto está cerrado',
        id: 'Proyek ini ditutup',
        fr: "Ce projet est fermé",
        de: "Dieses Projekt ist geschlossen",
        it: "Questo progetto è chiuso",
      },
      error_starting_response: {
        en: 'Unknown error starting response',
        pt: 'Resposta inicial de erro desconhecido',
        es: 'Respuesta de inicio de error desconocido',
        id: 'Respons awal kesalahan tidak dikenal',
        fr: "Erreur inconnue lors du démarrage de la réponse",
        de: "Unbekannter Fehler beim Starten der Antwort",
        it: "Errore sconosciuto durante l'avvio della risposta",
      },
      tap_to_continue: {
        en: 'Tap here to continue...',
        pt: 'Toque aqui para continuar...',
        es: 'Toca aquí para continuar...',
        id: 'Ketuk di sini untuk melanjutkan...',
        fr: "Appuyez ici pour continuer...",
        de: "Tippen Sie hier, um fortzufahren...",
        it: "Tocca qui per continuare...",
      },
      fallback_links: {
        en: "If you're still having trouble continuing, try tapping anywhere on the black bar at the bottom of the page to reveal the Next/Previous buttons.",
        pt: 'Se ainda estiver com problemas para continuar, tente tocar em qualquer lugar na barra preta na parte inferior da página para revelar os botões Próximo/Anterior.',
        es: 'Si aún tiene problemas para continuar, intente tocar en cualquier parte de la barra negra en la parte inferior de la página para mostrar los botones Siguiente/Anterior.',
        id: 'Jika Anda masih kesulitan melanjutkan, coba ketuk di mana saja pada bilah hitam di bagian bawah halaman untuk menampilkan tombol Berikutnya/Sebelumnya.',
        fr: "Si vous ne parvenez toujours pas à continuer, essayez d'appuyer n'importe où sur la barre noire en bas de la page pour afficher les boutons Suivant/Précédent.",
        de: "Wenn Sie weiterhin Probleme beim Fortfahren haben, tippen Sie irgendwo auf die schwarze Leiste unten auf der Seite, um die Schaltflächen „Weiter“/„Zurück“ anzuzeigen.",
        it: "Se hai ancora problemi a continuare, prova a toccare un punto qualsiasi della barra nera nella parte inferiore della pagina per visualizzare i pulsanti Successivo/Precedente.",
      },
      next: {
        en: 'Next',
        pt: 'Próximo',
        es: 'Próximo',
        id: 'Lanjut',
        fr: "Suivant",
        de: "Nächste",
        it: "Prossimo",
      }
    }[name][lang]
  };

  // figure out which platform we're on
  let platform;
  if (parsed.platform) {
    platform = parsed.platform;
  } else {
    let os = platformjs.os.toString();
    if (os.startsWith('Android')) {
      platform = 'android';
    } else if (os.startsWith('iOS')) {
      platform = 'ios';
    } else if (os.startsWith('OS X')) {
      platform = 'osx';
    } else if (os.startsWith('Windows')) {
      platform = 'windows';
    } else if (os.startsWith('Linux')) {
      platform = 'linux';
    } else {
      platform = 'unknown';
    }
  }

  if (embedded) {
    addBodyClasses(['job-client', 'embedded'])();
  } else {
    addBodyClasses(['job-client'])();
  }

  function isFinished() {
    return terminated || (job && currentPageIndex >= job.pages.length);
  }

  // hook for doing embedded-specific stuff
  useEffect(() => {
    if (embedded && !hasInitializedEmbed) {
      setHasInitializedEmbed(true);
      window.parent.postMessage({
        messageType: 'measure-embedded-job-message',
        action: 'initialized'
      }, '*');
    }
  }, [embedded, hasInitializedEmbed]);

  function loadJob(url, attributeWithSpec = null) {
    axios.get(url)
      .then((response) => {
        console.log("Response",response)

        if (attributeWithSpec) {
          setJob(response.data[attributeWithSpec]);
        } else {
          setJob(response.data);
        }

        setIsLoaded(true);
        if (!hasStarted) {
          setHasStarted(true);
          startJob();
        }
      })
      .catch((error) => {
        setError(error);
        setIsLoaded(true);
      });
  }

  // handler for job param changes
  useEffect(() => {
    if (exampleJobName) {
      // load a test job from S3
      loadJob(`https://measure-static.s3.us-east-1.amazonaws.com/workshop-job-examples/${exampleJobName}.json`, 'spec');
    } else if (profileJobName) {
      // load a profile survey from the Taxonomy server
      loadJob(`${TAXONOMY_BASE_URL}/api/v1/profile/categories/${profileJobName}`);
    } else {
      // load the job from workshop
      loadJob(baseJobURL, 'spec')
    }
  }, [uuid, srid, cid, platform]);

  // handler for page changes
  useEffect(() => {
    if (!job || !hasStarted) {
      return;
    }

    // check to see if we're at the end
    if (isFinished()) {
      // Only finish if we're not tracking to workshop, or if we have a responseUUID
      // (this prevents a race condition when goNext() is called before /started returns)
      if (!hasProcessedFinish && (!postTrackingToWorkshop || responseUUID)) {
        setHasProcessedFinish(true);
        finishJob('completed');
      }
    } else {

      // save the partial response to the server if we're beyond the first page
      if (currentPageIndex > 0) {
        updateJob();
      }

      // pick up the current page
      const newPage = job.pages[currentPageIndex];

      // if there is a page guard, evaluate it
      if (newPage.include_if) {
        let showPage = jsonLogic.apply(newPage.include_if, values);
        if (!showPage) {
          // the page isn't valid, so let's try the next one
          goNext();
          return;
        }
      }

      setValidatedQuestionList({});
      setNeedsValidation(true);
      setCompletedRequiredBlocks(false);
    }
  }, [currentPageIndex, job, hasStarted, responseUUID]);

  function startJob() {
    setHasStarted(true);
    if (postTrackingToWorkshop) {
      FingerprintJS.load()
        .then(fingerprintjs => {
          fingerprintjs.get()
            .then(result => {
              const fingerprint = result.visitorId;

              axios.post(`${baseJobURL}/started`, {
                srid: srid,
                cid: cid,
                platform: platform,
                lang: lang,
                run_type: runType,
                fingerprint: fingerprint,
                data: {values: values}
              })
                .then((response) => {
                  if (response.data.status === 'success') {
                    setResponseUUID(response.data.uuid);
                  } else {
                    if (response.data.message === 'project_not_running') {
                      setError({message: `${getString('project_not_running', lang)} (${srid})`});
                    } else if (response.data.message === 'project_closed') {
                      setError({message: `${getString('project_closed', lang)} (${srid})`});
                    } else {
                      setError({message: getString('error_starting_response', lang)});
                    }
                  }
                })
                .catch((error) => {
                  setError(error);
                });
            });
        });
    }
  }

  function updateJob() {
    if (postTrackingToWorkshop) {
      axios.post(`${baseJobURL}/in-progress`, {
        uuid: responseUUID,
        data: {values: values}
      })
        .catch((error) => {
          setError(error);
        });
    }
  }

  const startFallbackTimer = (completionStatus) => {
    setTimeout(() => {
      setFallbackLinkCompletionStatus(completionStatus);
    }, 2000);
  };

  const postFinishIframeMessage = (completionStatus) => {
    if (completionStatus === 'completed') {
      window.parent.postMessage({
        messageType: 'measure-embedded-job-message',
        action: 'job-completed',
        data: values,
      }, '*');
    } else if (completionStatus === 'terminated') {
      window.parent.postMessage({
        messageType: 'measure-embedded-job-message',
        action: 'job-terminated',
        errorMessage: 'Error during screen capture',
        errorStatus: 'SCREEN_CAPTURE_ERROR'
      }, '*');
    } else if (completionStatus === 'user-terminated') {
      window.parent.postMessage({
        messageType: 'measure-embedded-job-message',
        action: 'job-user-terminated',
        errorMessage: 'User terminated job',
        errorStatus: 'USER_TERMINATION'
      }, '*');
    }
  };

  const finishEmbeddedExperience = (completionStatus) => {
    postFinishIframeMessage(completionStatus);
    startFallbackTimer(completionStatus);
  };

  function finishJob(completionStatus) {
    if (completionStatus === 'completed') {
      if (postTrackingToWorkshop) {
        axios.post(`${baseJobURL}/completed`, {
          uuid: responseUUID,
          data: {values: values}
        })
          .catch((error) => {
            setError(error);
          })
          .finally (() => {
            if (embedded) {
              finishEmbeddedExperience(completionStatus);
            }
          });
      } else {
        if (embedded) {
          finishEmbeddedExperience(completionStatus);
        }
      }
    } else if (completionStatus === 'terminated') {
      if (postTrackingToWorkshop) {
        axios.post(`${baseJobURL}/terminated`, {
          uuid: responseUUID,
        })
          .catch((error) => {
            setError(error);
          })
          .finally(() => {
            if (embedded) {
              finishEmbeddedExperience(completionStatus);
            }
          });
      } else {
        if (embedded) {
          finishEmbeddedExperience(completionStatus);
        }
      }
    } else if (completionStatus === 'user-terminated') {
      if (postTrackingToWorkshop) {
        axios.post(`${baseJobURL}/user-terminated`, {
          uuid: responseUUID,
        })
          .catch(function (error) {
            setError(error);
          })
          .finally(() => {
            if (embedded) {
              finishEmbeddedExperience(completionStatus);
            }
          });
      } else {
        if (embedded) {
          finishEmbeddedExperience(completionStatus);
        }
      }
    }
  }

  function handleInputChange(event) {
    const target = event.target;
    const name = target.name;
    const newValue = target.value;

    if (['radio', 'text'].includes(target.type)) {
      // replace the value outright
      setValues({...values, [name]: newValue});
    } else if (target.type === 'checkbox') {
      let allValuesForItem = values[name] || [];
      if (allValuesForItem.includes(newValue)) {
        // remove the item from the list
        allValuesForItem = allValuesForItem.filter(v => v !== newValue);
      } else {
        // otherwise, add it
        allValuesForItem.push(newValue);
      }
      setValues({...values, [name]: allValuesForItem});
    }

    setNeedsValidation(true);
  }

  const goNext = () => {
    setCurrentPageIndex(currentPageIndex + 1);
  };

  const handleTerminate = () => {
    setTerminated(true);
    finishJob('terminated');
  };

  const handleUserTerminate = () => {
    setTerminated(true);
    finishJob('user-terminated');
  };

  const handleFallbackLinkClick = () => {
    postFinishIframeMessage(fallbackLinkCompletionStatus);
    setFallbackLinkClicks(fallbackLinkClicks + 1);
  };

  const isPostalCodeQuestion = (questionId) => {
    const postalCodeItems = [
      'ca_postal_code_of_residence_string',
      'uk_postal_code_of_residence_string',
      'us_zip_code_of_residence_string',
      'au_postal_code_of_residence_string'
    ]

    return postalCodeItems.includes(questionId);
  }

  useEffect(() => {
    if (!isLoaded) return;
    if (currentPageIndex >= job.pages.length) return;

    const page = job.pages[currentPageIndex];

    for (const block of page.blocks) {
      if (block.type === 'question' && (block.question.required || isProfileSurvey)) {
        if (['checkbox', 'radio'].includes(block.question.widget)) {

          // basic check that there is at last one value for the question
          const foundValue = block.question.id in values && values[block.question.id].length > 0;
          setValidatedQuestionList({...validatedQuestionList, [block.question.id]: foundValue});

        } else if (['single_line_text'].includes(block.question.widget)) {

          //
          // Special case code for postal code validation
          //


          if (isPostalCodeQuestion(block.question.id)) {
            const value = values[block.question.id];

            if (value) {
              setQuestionValidationStatus('validating');

              axios.post(`${TAXONOMY_BASE_URL}/api/v1/geo/resolve-postal-code`, {
                country: values['country_of_residence'],
                postal_code: value,
              })
                  .then((response) => {
                    if (response.data.found) {
                      setValues({...values, ...response.data.items})
                      setValidatedQuestionList({...validatedQuestionList, [block.question.id]: true});
                      setQuestionValidationStatus('valid');
                    } else {
                      setValidatedQuestionList({...validatedQuestionList, [block.question.id]: false});
                      setQuestionValidationStatus('invalid');
                    }
                  })
                  .catch((error) => {
                    setValidatedQuestionList({...validatedQuestionList, [block.question.id]: false});
                    setQuestionValidationStatus('invalid');
                  });
            }
          } else {
            // basic check that there is at last one value for the question
            const foundValue = block.question.id in values && values[block.question.id];
            setValidatedQuestionList({...validatedQuestionList, [block.question.id]: foundValue});
          }
        }
      }
    }

    setNeedsValidation(false);

  }, [needsValidation, isLoaded]);

  useEffect(() => {
    if (!isLoaded) return;
    if (currentPageIndex >= job.pages.length) return;

    const page = job.pages[currentPageIndex];

    for (const block of page.blocks) {
      if (block.type === 'question' && (block.question.required || isProfileSurvey)) {
        if (!validatedQuestionList[block.question.id]) {
          setCompletedRequiredBlocks(false);
          return;
        }
      }
    }

    setCompletedRequiredBlocks(true);

  }, [validatedQuestionList]);

  // construct the list of classes for the main job container
  let containerClass = "job-container";
  if (embedded) {
    containerClass += ' embedded';
  }
  if (embeddingHost) {
    containerClass += ' ' + embeddingHost;
  }

  const FullPageSpinner = () => (
    <Container fluid="sm" className={containerClass}>
      <Row>
        <Col>
          <div className="full-page-message">
            <Spinner className='full-page-spinner' type="grow" />
          </div>
        </Col>
      </Row>
    </Container>
  );

  if (error) {
    return (
      <Container fluid="sm" className={containerClass}>
        <Row>
          <Col>
            <p className="text-center">Error: {error.message}</p>
          </Col>
        </Row>
      </Container>
    );
  } else if (!isLoaded) {
    return <FullPageSpinner />;
  } else if (isFinished()) {
    console.log('in isfinished');
    if (embedded) {
      if (fallbackLinkCompletionStatus) {
          console.log('in fallbackLinkCompletionStatus');

          if (redirect === 'true') {
            if (partial_payment === 'true') {
                window.location.href = '/mobile-complete-partial-payment.html';
                return null;
            }
            else {
                window.location.href = '/mobile-complete-full-payment.html';
                return null;
            }

          }
          else {
              return (
                  <Container fluid="sm" className={containerClass}>
                      <Row>
                          <Col>
                              <p className="text-center">
                                  <a onClick={handleFallbackLinkClick} href="#">
                                      {getString('tap_to_continue', lang)}
                                  </a>
                              </p>
                              {fallbackLinkClicks > 1 &&
                              <p className="text-center">
                                  {getString('fallback_links', lang)}
                              </p>
                              }
                          </Col>
                      </Row>
                  </Container>
              );
          }
      } else {
          console.log('in fullpagespinner');
        return <FullPageSpinner />;
      }
    } else {
      return (
        <Container fluid="sm" className={containerClass}>
          <Row>
            <Col>
              <p className="text-center">{getString('finished_the_job', lang)}</p>
            </Col>
          </Row>
        </Container>
      );
    }
  } else {
    // get the current page
    const page = job.pages[currentPageIndex];

    // Defensive guard in case page is undefined
    if (!page) {
      return <FullPageSpinner />;
    }

    return (
      <JobContext.Provider value={{platform, lang, baseJobURL, values, setValues, goNext, handleTerminate, handleUserTerminate, embedded, embeddingHost, cid, srid}}>
        <Container fluid="sm" className={containerClass}>
          <Row>
            <Col>
              {page.blocks.map((block, index) => (
                <React.Fragment key={index}>

                  {block.type === 'question' &&
                    <div className="question-block" key={block.question.id}>

                      {block.question.question &&
                        <p className="question">{block.question.question.en}</p>
                      }

                      {block.question.widget === 'checkbox' &&
                        <div className="choices-container">
                          {block.question.choices.map((choice, index) => {
                            const choiceID = `${block.question.id}_${choice.value}`;
                            const checked = block.question.id in values && values[block.question.id].includes(choice.value);
                            return (
                              <label
                                key={choice.value}
                                className={'choice ' + (checked ? 'checked' : '')}
                                htmlFor={choiceID}
                              >
                                <div className="choice-widget">
                                  <div className="custom-control custom-checkbox">
                                    <input
                                      type="checkbox"
                                      id={choiceID}
                                      name={block.question.id}
                                      value={choice.value}
                                      className="custom-control-input"
                                      onChange={handleInputChange}
                                      checked={checked}
                                    />
                                    <label
                                      className="custom-control-label"
                                      htmlFor={choiceID}
                                    />
                                  </div>
                                </div>
                                <div className="choice-label">{choice.label.en}</div>
                              </label>
                            );
                          })}
                        </div>
                      }

                      {block.question.widget === 'radio' &&
                        <div className="choices-container">
                          {block.question.choices.map((choice, index) => {
                            const choiceID = `${block.question.id}_${choice.value}`;
                            const checked = values[block.question.id] === choice.value;
                            return (
                              <label
                                key={choice.value}
                                className={'choice ' + (checked ? 'checked' : '')}
                                htmlFor={choiceID}
                              >
                                <div className="choice-widget">
                                  <div className="custom-control custom-radio">
                                    <input
                                      type="radio"
                                      id={choiceID}
                                      name={block.question.id}
                                      value={choice.value}
                                      className="custom-control-input"
                                      onChange={handleInputChange}
                                      checked={checked}
                                    />
                                    <label
                                      className="custom-control-label"
                                      htmlFor={choiceID}
                                    />
                                  </div>
                                </div>
                                <div className="choice-label">{choice.label.en}</div>
                              </label>
                            );
                          })}
                        </div>
                      }

                      {block.question.widget === 'single_line_text' &&
                        <div className="single-line-text-container">
                          <input
                            type="text"
                            id={block.question.id}
                            name={block.question.id}
                            className="single-line-text-input"
                            autoComplete="off"
                            value={values[block.question.id] || ''}
                            onChange={handleInputChange}
                          />

                          {values[block.question.id] && isPostalCodeQuestion(block.question.id) &&
                            <>
                              {questionValidationStatus === 'validating' &&
                                <Spinner className="text-input-validation-spinner" type="border" />
                              }
                              {questionValidationStatus === 'valid' &&
                                <div className="validation-icon valid"></div>
                              }
                              {questionValidationStatus === 'invalid' &&
                                <div className="validation-icon invalid"></div>
                              }
                            </>
                          }

                        </div>
                      }
                    </div>
                  }

                  {(block.type === 'screen_capture' || block.type === 'android_app_usage_capture') &&
                    <ScreenCaptureBlock block={block}/>
                  }

                  {block.type === 'content' &&
                    <div className="content-block" dangerouslySetInnerHTML={{__html: block.content}} />
                  }


                  {block.type === 'kyc' &&
                    <SumSubInstructions cid={cid} flow={block.kyc?.flow} lang={lang}/>
                  }

                </React.Fragment>
              ))}

              {!page.hide_next_button &&
                <div className="action-button text-center">
                  <Button
                    className={"next-button " + (completedRequiredBlocks ? "" : "disabled")}
                    disabled={!completedRequiredBlocks}
                    color="primary"
                    onClick={() => goNext()}
                  >
                    {getString('next', lang)}
                  </Button>
                </div>
              }

            </Col>
          </Row>

          {debug &&
            <Row>
              <Col>
                <div className="job-debugger">
                  <pre>currentPageIndex = {currentPageIndex}</pre>
                  <pre>values = {JSON.stringify(values, null, 2)}</pre>
                </div>
              </Col>
            </Row>
          }
        </Container>
      </JobContext.Provider>
    );
  }
}

export default JobClient;
